// MyTeamPage.js — COMPANY MODEL (modern UI redesign)
//
// Behaviour, handlers, and the GET /connections data contract are unchanged
// from the previous version — only the markup/structure and styling are
// modernised. Role + admin-ness still come from GET /connections
// (myRole / isAdmin); the platform x-user-role header is still sent.

import React, { useState, useEffect, useContext } from 'react';
import { auth } from '../firebaseConfig';
import { API_BASE_URL } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import GuideModal from './GuideModal';
import './PageLayout.css';
import './MyTeamPage.css';

const ROLE_LABELS = {
  admin:    'Admin',
  reviewer: 'Reviewer',
  user:     'User',
};

const ROLE_BLURB = {
  admin:    'Full control — can manage materials, review, and manage members & roles.',
  reviewer: 'Can do everything an admin can, except manage members and roles.',
  user:     'Can view, build, save drafts, and send materials for review.',
};

// Deterministic avatar colour from an email, so each person keeps a stable hue.
const AVATAR_COLORS = [
  '#4A9B8F', '#6366f1', '#0ea5e9', '#f59e0b',
  '#ec4899', '#8b5cf6', '#10b981', '#ef4444',
];
const avatarColor = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const initial = (str = '') => (str.trim()[0] || '?').toUpperCase();

export default function MyTeamPage() {
  const { user } = useContext(AuthContext);
  const { selectedRegion } = useRegion();

  const [company, setCompany]                 = useState(null);
  const [myRole, setMyRole]                   = useState(null);
  const [isAdmin, setIsAdmin]                 = useState(false);
  const [members, setMembers]                 = useState([]);
  const [pendingReceived, setPendingReceived] = useState([]);

  const [inviteEmail, setInviteEmail] = useState('');
  const [resendingId, setResendingId] = useState(null);
  const [inviteRole, setInviteRole]   = useState('user');

  const [newCompanyName, setNewCompanyName] = useState('');
  const [creating, setCreating]             = useState(false);
  // Edit company name (admin)
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  // Logo upload (admin)
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage]       = useState(null);

  // Permissions guide modal
  const [showGuide, setShowGuide] = useState(false);

  const getHeaders = async () => {
    const token = await auth.currentUser?.getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'x-user-email': user?.email || '',
      'x-user-role': user?.role || 'user',
      'x-region': selectedRegion || 'vquest',
    };
  };

  const load = async () => {
    setLoading(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE_URL}/connections`, { headers });
      const data = await res.json();
      setCompany(data.company || null);
      setMyRole(data.myRole || null);
      setIsAdmin(!!data.isAdmin);
      setMembers(data.members || []);
      setPendingReceived(data.pendingReceived || []);
    } catch (err) {
      console.error('Error loading company members:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); }, [user]);

  // sticky=true keeps the banner up until the next action — used for delivery
  // failures, which the user needs to actually read and act on.
  const showMessage = (type, text, sticky = false) => {
    setMessage({ type, text });
    if (!sticky) setTimeout(() => setMessage(null), 4000);
  };

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) return;
    setCreating(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE_URL}/connections/company`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: newCompanyName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create company');
      setNewCompanyName('');
      showMessage('success', `Created “${data.company.Name}”. You're the admin.`);
      load();
    } catch (err) {
      showMessage('error', err.message);
    } finally {
      setCreating(false);
    }
  };

 const startEditName = () => {
    setEditNameValue(company?.Name || '');
    setEditingName(true);
  };
 
  const handleRenameCompany = async () => {
    const name = editNameValue.trim();
    if (!name || name === company?.Name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE_URL}/connections/company`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to rename company');
      showMessage('success', 'Company name updated.');
      setEditingName(false);
      load();
    } catch (err) {
      showMessage('error', err.message);
    } finally {
      setSavingName(false);
    }
  };
  
const handleLogoChange = async (e) => {
  const file = e.target.files?.[0];
  e.target.value = ''; // allow re-selecting the same file later
  if (!file) return;
  setUploadingLogo(true);
  try {
    const token = await auth.currentUser?.getIdToken();
    const formData = new FormData();
    formData.append('logo', file);

  // NB: no Content-Type header — the browser sets the multipart boundary.
  const res = await fetch(`${API_BASE_URL}/connections/company/logo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-user-email': user?.email || '',
      'x-user-role': user?.role || 'user',
      'x-region': selectedRegion || 'vquest',
    },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to upload logo');
  showMessage('success', 'Company logo updated.');
  load();
} catch (err) {
  showMessage('error', err.message);
} finally {
  setUploadingLogo(false);
}
};

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSubmitting(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE_URL}/connections/invite`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          inviteeEmail: inviteEmail.trim().toLowerCase(),
          role: inviteRole,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send invite');
      // The member row is created even when the email bounces, so trust the
      // server's emailSent flag rather than assuming a 200 means "delivered".
      if (data.emailSent === false) {
        showMessage(
          'error',
          `${inviteEmail} was added as ${ROLE_LABELS[inviteRole]}, but the invite email could not be delivered${data.emailError ? ` (${data.emailError})` : ''}. Use Resend under Sent Invites to try again.`,
          true
        );
      } else {
        showMessage('success', `Invite sent to ${inviteEmail} as ${ROLE_LABELS[inviteRole]}.`);
      }
      setInviteEmail('');
      setInviteRole('user');
      load();
    } catch (err) {
      showMessage('error', err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (id, email) => {
    setResendingId(id);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE_URL}/connections/${id}/resend`, { method: 'POST', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to resend invite');
      if (data.emailSent === false) {
        showMessage('error', `Could not deliver the invite to ${email}${data.emailError ? ` (${data.emailError})` : ''}.`, true);
      } else {
        showMessage('success', `Invite re-sent to ${email}.`);
      }
      load();
    } catch (err) {
      showMessage('error', err.message);
    } finally {
      setResendingId(null);
    }
  };

  const handleAccept = async (id) => {
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE_URL}/connections/${id}/accept`, { method: 'POST', headers });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed');
      showMessage('success', 'Joined the company!');
      load();
    } catch (err) {
      showMessage('error', err.message);
    }
  };

  const handleReject = async (id) => {
    try {
      const headers = await getHeaders();
      await fetch(`${API_BASE_URL}/connections/${id}/reject`, { method: 'POST', headers });
      showMessage('success', 'Invite declined.');
      load();
    } catch (err) {
      showMessage('error', 'Failed to decline');
    }
  };

  const handleRoleChange = async (id, role) => {
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE_URL}/connections/${id}/role`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ role }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change role');
      showMessage('success', `Role updated to ${ROLE_LABELS[role]}.`);
      load();
    } catch (err) {
      showMessage('error', err.message);
    }
  };

  const handleRemove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the company? They'll lose access to shared materials.`)) return;
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_BASE_URL}/connections/${id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove');
      showMessage('success', 'Member removed.');
      load();
    } catch (err) {
      showMessage('error', err.message);
    }
  };

  const isMe = (email) => email?.toLowerCase() === user?.email?.toLowerCase();

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="mt-page">
      <div className="mt-loading">
        <div className="mt-spinner" />
        <p>Loading your team…</p>
      </div>
    </div>
  );

  const activeMembers  = members.filter((m) => m.Status === 'active');
  const invitedMembers = members.filter((m) => m.Status === 'invited');

  // "sent 3 days ago" on a pending invite — an admin can then spot a stale one
  // and resend rather than assuming it landed. UpdatedAt moves on each resend.
  const sentAgo = (m) => {
    const when = new Date(m.UpdatedAt || m.CreatedAt);
    if (isNaN(when)) return null;
    const days = Math.floor((Date.now() - when.getTime()) / 86400000);
    if (days <= 0) return 'sent today';
    if (days === 1) return 'sent yesterday';
    return `sent ${days} days ago`;
  };

  // Mirrors INVITE_TTL_DAYS in the backend (routes/connections.js). An expired
  // invite stops auto-joining and stops blocking the person from creating their
  // own company; Resend revives it.
  const INVITE_TTL_DAYS = 14;
  const inviteExpiry = (m) => {
    const when = new Date(m.UpdatedAt || m.CreatedAt);
    if (isNaN(when)) return null;
    const daysLeft = INVITE_TTL_DAYS - Math.floor((Date.now() - when.getTime()) / 86400000);
    if (daysLeft <= 0) return 'expired — resend to revive';
    if (daysLeft === 1) return 'expires tomorrow';
    return `expires in ${daysLeft} days`;
  };

  const RolePill = ({ role }) => (
    <span className={`mt-pill mt-pill--${role}`}>{ROLE_LABELS[role] || role}</span>
  );

  const Banner = () =>
    message ? (
      <div className={`mt-banner mt-banner--${message.type}`}>
        <span className="mt-banner__icon">{message.type === 'success' ? '✓' : '!'}</span>
        <span>{message.text}</span>
      </div>
    ) : null;

  // ── Membership-less state ─────────────────────────────────────────────────
  if (!company) {
    return (
      <div className="mt-page">
        <header className="mt-hero">
          <h1>My Team</h1>
          <p>You're not part of a company yet.</p>
        </header>

        <Banner />

        {pendingReceived.length > 0 && (
          <section className="mt-card">
            <div className="mt-card__head">
              <h2>Pending Invites</h2>
              <span className="mt-count mt-count--amber">{pendingReceived.length}</span>
            </div>
            <div className="mt-list">
              {pendingReceived.map((inv) => (
                <div key={inv.Id} className="mt-row mt-row--invite">
                  <div className="mt-avatar" style={{ background: avatarColor(inv.CompanyName) }}>
                    {initial(inv.CompanyName)}
                  </div>
                  <div className="mt-row__info">
                    <div className="mt-row__title">{inv.CompanyName}</div>
                    <div className="mt-row__sub">
                      Invited you as <RolePill role={inv.CompanyRole} />
                      {inv.InvitedBy ? <span className="mt-muted"> · by {inv.InvitedBy}</span> : null}
                    </div>
                  </div>
                  <div className="mt-row__actions">
                    <button className="mt-btn mt-btn--primary" onClick={() => handleAccept(inv.Id)}>Accept</button>
                    <button className="mt-btn mt-btn--ghost"  onClick={() => handleReject(inv.Id)}>Decline</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-card">
          <div className="mt-card__head">
            <h2>Create a Company</h2>
          </div>
          <div className="mt-note mt-note--warn">
            <span className="mt-note__icon">⚠️</span>
            <span>
              Only create a company if you're the first person from your organisation here.
              If a colleague already set one up, ask them to invite you instead — don't create a duplicate.
            </span>
          </div>
          <div className="mt-inputbar">
            <input
              type="text"
              className="mt-input"
              placeholder="e.g. Design Inc Australia"
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateCompany()}
            />
            <button
              className="mt-btn mt-btn--primary"
              onClick={handleCreateCompany}
              disabled={creating || !newCompanyName.trim()}
            >
              {creating ? 'Creating…' : 'Create Company'}
            </button>
          </div>
          <p className="mt-hint">You'll become the admin and can invite team members by email.</p>
        </section>
      </div>
    );
  }

  // ── In-company state ──────────────────────────────────────────────────────
  return (
    <div className="mt-page">

      {/* Company header */}
      <header className="mt-company">
        <div className="mt-company__logowrap">
          {company.LogoUrl ? (
            <img
              src={company.LogoUrl}
              alt={`${company.Name} logo`}
              className="mt-company__logoimg"
            />
          ) : (
            <div className="mt-company__logo" style={{ background: avatarColor(company.Name) }}>
              {initial(company.Name)}
            </div>
          )}

          {isAdmin && (
            <label className="mt-logo-edit" title="Change logo">
              {uploadingLogo ? '…' : '📷'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                onChange={handleLogoChange}
                disabled={uploadingLogo}
                style={{ display: 'none' }}
              />
            </label>
          )}
        </div>

        <div className="mt-company__info">
          {editingName ? (
            <div className="mt-name-edit">
              <input
                type="text"
                className="mt-input"
                value={editNameValue}
                onChange={(e) => setEditNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameCompany();
                  if (e.key === 'Escape') setEditingName(false);
                }}
                autoFocus
              />
              <button
                className="mt-btn mt-btn--primary"
                onClick={handleRenameCompany}
                disabled={savingName || !editNameValue.trim()}
              >
                {savingName ? 'Saving…' : 'Save'}
              </button>
              <button className="mt-btn mt-btn--ghost" onClick={() => setEditingName(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <h1 className="mt-company__name">
              {company.Name}
              {isAdmin && (
                <button
                  className="mt-name-editbtn"
                  onClick={startEditName}
                  title="Edit company name"
                >
                  ✏️
                </button>
              )}
            </h1>
          )}
          <p>
            {activeMembers.length} member{activeMembers.length === 1 ? '' : 's'}
            {company.EmailDomain ? <span className="mt-muted"> · @{company.EmailDomain}</span> : null}
          </p>
        </div>

        <div className="mt-company__role">
          <span className="mt-muted">Your role</span>
          <RolePill role={myRole} />
          <button className="mt-guide-btn" onClick={() => setShowGuide(true)}>
            📖 How to?
          </button>
        </div>
      </header>

      <GuideModal
        open={showGuide}
        onClose={() => setShowGuide(false)}
        src="/guides/Nezo_User_Permissions_Guide.html"
        title="How my team works — user permissions guide"
      />

      <Banner />

      {/* Pending invites addressed to me */}
      {pendingReceived.length > 0 && (
        <section className="mt-card">
          <div className="mt-card__head">
            <h2>Pending Invites</h2>
            <span className="mt-count mt-count--amber">{pendingReceived.length}</span>
          </div>
          <div className="mt-list">
            {pendingReceived.map((inv) => (
              <div key={inv.Id} className="mt-row mt-row--invite">
                <div className="mt-avatar" style={{ background: avatarColor(inv.CompanyName) }}>
                  {initial(inv.CompanyName)}
                </div>
                <div className="mt-row__info">
                  <div className="mt-row__title">{inv.CompanyName}</div>
                  <div className="mt-row__sub">
                    Invited you as <RolePill role={inv.CompanyRole} />
                  </div>
                </div>
                <div className="mt-row__actions">
                  <button className="mt-btn mt-btn--primary" onClick={() => handleAccept(inv.Id)}>Accept</button>
                  <button className="mt-btn mt-btn--ghost"  onClick={() => handleReject(inv.Id)}>Decline</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Invite (admin only) */}
      {isAdmin && (
        <section className="mt-card">
          <div className="mt-card__head">
            <h2>Invite a Member</h2>
          </div>
          <p className="mt-hint mt-hint--top">
            Enter their email and choose a role. They'll get an email invitation to join.
          </p>
          <div className="mt-inputbar">
            <input
              type="email"
              className="mt-input"
              placeholder="colleague@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
            />
            <select
              className="mt-select"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
            >
              <option value="user">User</option>
              <option value="reviewer">Reviewer</option>
              <option value="admin">Admin</option>
            </select>
            <button
              className="mt-btn mt-btn--primary"
              onClick={handleInvite}
              disabled={submitting || !inviteEmail.trim()}
            >
              {submitting ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
          <p className="mt-role-blurb">{ROLE_BLURB[inviteRole]}</p>
        </section>
      )}

      {/* Members */}
      <section className="mt-card">
        <div className="mt-card__head">
          <h2>Members</h2>
          <span className="mt-count">{activeMembers.length}</span>
        </div>

        {activeMembers.length === 0 ? (
          <div className="mt-empty">No active members yet.</div>
        ) : (
          <div className="mt-list">
            {activeMembers.map((m) => (
              <div key={m.Id} className="mt-row">
                <div className="mt-avatar" style={{ background: avatarColor(m.UserEmail) }}>
                  {initial(m.UserEmail)}
                </div>
                <div className="mt-row__info">
                  <div className="mt-row__title">
                    {m.UserEmail}
                    {isMe(m.UserEmail) && <span className="mt-you">You</span>}
                  </div>
                  <div className="mt-row__sub mt-muted">
                    Since {new Date(m.CreatedAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="mt-row__actions">
                  {isAdmin ? (
                    <>
                      <select
                        className="mt-select mt-select--sm"
                        value={m.CompanyRole}
                        onChange={(e) => handleRoleChange(m.Id, e.target.value)}
                      >
                        <option value="user">User</option>
                        <option value="reviewer">Reviewer</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button
                        className="mt-btn mt-btn--danger-ghost"
                        onClick={() => handleRemove(m.Id, m.UserEmail)}
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <RolePill role={m.CompanyRole} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Sent invites (admin only) */}
      {isAdmin && invitedMembers.length > 0 && (
        <section className="mt-card">
          <div className="mt-card__head">
            <h2>Sent Invites</h2>
            <span className="mt-count">{invitedMembers.length}</span>
          </div>
          <div className="mt-list">
            {invitedMembers.map((m) => (
              <div key={m.Id} className="mt-row mt-row--muted">
                <div className="mt-avatar mt-avatar--ghost">{initial(m.UserEmail)}</div>
                <div className="mt-row__info">
                  <div className="mt-row__title mt-muted">{m.UserEmail}</div>
                  <div className="mt-row__sub">
                    <RolePill role={m.CompanyRole} /> · awaiting response
                    {sentAgo(m) ? ` · ${sentAgo(m)}` : ''}
                    {inviteExpiry(m) ? ` · ${inviteExpiry(m)}` : ''}
                  </div>
                </div>
                <div className="mt-row__actions">
                  <span className="mt-tag">Pending</span>
                  <button
                    className="mt-btn mt-btn--ghost"
                    disabled={resendingId === m.Id}
                    onClick={() => handleResend(m.Id, m.UserEmail)}
                    title="Send the invite email again"
                  >
                    {resendingId === m.Id ? 'Sending…' : 'Resend'}
                  </button>
                  <button
                    className="mt-btn mt-btn--danger-ghost"
                    onClick={() => handleRemove(m.Id, m.UserEmail)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

    </div>
  );
}